import { AnalyticsService } from '@alison/analytics/client';
import GridViewOutlined from '@mui/icons-material/GridViewOutlined';
import TableRowsOutlined from '@mui/icons-material/TableRowsOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { type ReactNode, useMemo, useState } from 'react';
import { ExportCsvButton } from '../../creatives/ExportCsvButton';
import { TOGGLE_BUTTON_SX } from '../../creatives/utils';
import { CompetitionCreativesGrid } from './CompetitionCreativesGrid';
import { CompetitionCreativesTable } from './CompetitionCreativesTable';
import { type CompetitionCreativesScope, useCompetitionCreativesList } from './data/api';
import { competitionCreativesToCsv, downloadCsv } from './data/csv';
import { CompetitionCreativeDrawer } from './detail/CompetitionCreativeDrawer';

const SORTS = {
  newest: { field: 'firstSeenDate', direction: 'desc' },
  longestRunning: { field: 'activeDays', direction: 'desc' },
} as const satisfies Record<string, CompetitionCreativesScope['sort']>;

type BoardView = 'grid' | 'table';
type BoardSort = keyof typeof SORTS;

interface CompetitionCreativesBoardProps {
  accountIds: number[];
  dateWindow: [string, string];
  selected: CompetitionCreativesScope['selected'];
}

export function CompetitionCreativesBoard({
  accountIds,
  dateWindow,
  selected,
}: CompetitionCreativesBoardProps): ReactNode {
  const [view, setView] = useState<BoardView>('grid');
  const [sort, setSort] = useState<BoardSort>('newest');
  const [taggedOnly, setTaggedOnly] = useState(false);
  const [openCreativeId, setOpenCreativeId] = useState<string | null>(null);

  const scope = useMemo<CompetitionCreativesScope>(
    () => ({
      dateWindow,
      selected,
      sort: SORTS[sort],
      // Filtered server-side, before the row cap, so toggling this can't silently under-report
      // tagged creatives ranked outside an already-capped page.
      ...(taggedOnly ? { taggedOnly } : {}),
      ...(accountIds.length ? { accountIds } : {}),
    }),
    [accountIds, dateWindow, selected, sort, taggedOnly],
  );

  const list = useCompetitionCreativesList(scope, accountIds.length > 0);
  // `isPending` stays true forever for a query disabled via `enabled: false` (no accounts in
  // scope), since it never even starts — it isn't "still fetching".
  const pending = list.isPending && accountIds.length > 0;

  function exportCsv(): void {
    if (list.data === undefined) return;
    AnalyticsService.track('Competition Creatives - CSV Exported');
    downloadCsv(
      `competition_creatives_${dateWindow[0]}_${dateWindow[1]}.csv`,
      competitionCreativesToCsv(list.data),
    );
  }

  return (
    <Box sx={{ px: 6, py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={taggedOnly}
              onChange={(event) => {
                AnalyticsService.track('Competition Creatives - Tagged Only Toggled', {
                  enabled: event.target.checked,
                });
                setTaggedOnly(event.target.checked);
              }}
            />
            <Typography variant="body2" color="text.primary">
              Tagged only
            </Typography>
          </Box>
          <ToggleButtonGroup
            exclusive
            value={sort}
            onChange={(_event, next: unknown) => {
              if (next === 'newest' || next === 'longestRunning') {
                AnalyticsService.track('Competition Creatives - Sort Changed', { sort: next });
                setSort(next);
              }
            }}
            sx={{ backgroundColor: 'background.page' }}
          >
            <ToggleButton value="newest" sx={TOGGLE_BUTTON_SX}>
              Newest
            </ToggleButton>
            <ToggleButton value="longestRunning" sx={TOGGLE_BUTTON_SX}>
              Running the longest
            </ToggleButton>
          </ToggleButtonGroup>
          <ExportCsvButton
            dataTest="competition-creatives-export-csv"
            disabled={list.data === undefined}
            onClick={exportCsv}
          />
        </Box>
        <ToggleButtonGroup
          exclusive
          value={view}
          onChange={(_event, next: unknown) => {
            if (next === 'table' || next === 'grid') {
              AnalyticsService.track('Competition Creatives - View Changed', { view: next });
              setView(next);
            }
          }}
          sx={{ backgroundColor: 'background.page' }}
        >
          <ToggleButton value="grid" sx={TOGGLE_BUTTON_SX}>
            <GridViewOutlined sx={{ fontSize: 16, mr: 1 }} />
            Grid
          </ToggleButton>
          <ToggleButton value="table" sx={TOGGLE_BUTTON_SX}>
            <TableRowsOutlined sx={{ fontSize: 16, mr: 1 }} />
            Table
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {list.error ? (
        <Alert severity="error" sx={{ mt: 4 }}>
          {list.error.message}
        </Alert>
      ) : (
        <Box sx={{ mt: 4 }}>
          {view === 'grid' ? (
            <CompetitionCreativesGrid
              data={list.data}
              pending={pending}
              onOpen={setOpenCreativeId}
            />
          ) : (
            <CompetitionCreativesTable
              data={list.data}
              pending={pending}
              onOpen={setOpenCreativeId}
            />
          )}
        </Box>
      )}
      <CompetitionCreativeDrawer
        creatives={list.data?.creatives ?? []}
        creativeId={openCreativeId}
        accountIds={accountIds}
        onOpen={setOpenCreativeId}
        onClose={() => setOpenCreativeId(null)}
      />
    </Box>
  );
}
